import React, { useState, useEffect, useMemo } from 'react';
import { md5 } from '../utils/md5';

interface AvatarProps {
  name: string;
  email?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES = {
  sm: 'w-6 h-6 text-xs',
  md: 'w-8 h-8 text-sm',
  lg: 'w-10 h-10 text-base',
};

// A curated list of good-looking, distinct colors from Tailwind's palette
const COLORS = [
  'bg-red-500', 'bg-orange-500', 'bg-amber-500', 
  'bg-lime-500', 'bg-green-500', 'bg-emerald-500', 'bg-teal-500',
  'bg-cyan-500', 'bg-sky-500', 'bg-blue-500', 'bg-indigo-500',
  'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500',
  'bg-rose-500'
];

// Simple hash function to get a consistent color based on name
const nameToColor = (name: string): string => {
  if (!name) return COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }
  const index = Math.abs(hash) % COLORS.length;
  return COLORS[index];
};

const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length > 1) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const Avatar: React.FC<AvatarProps> = ({ name, email, size = 'md', className = '' }) => {
  const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  
  const gravatarUrl = useMemo(() => {
    if (!email) return null;
    const hash = md5(email.trim().toLowerCase());
    const sizeInPixels = size === 'sm' ? 24 : size === 'lg' ? 40 : 32;
    // Request 2x image size for better quality on high-resolution displays
    return `https://www.gravatar.com/avatar/${hash}?d=404&s=${sizeInPixels * 2}`;
  }, [email, size]);

  useEffect(() => {
    if (!gravatarUrl) {
        setImageStatus('error');
        return;
    }
    setImageStatus('loading');
    const img = new Image();
    img.src = gravatarUrl;
    img.onload = () => setImageStatus('loaded');
    img.onerror = () => setImageStatus('error');
  }, [gravatarUrl]);

  const sizeClass = SIZES[size];
  
  // If the image has loaded successfully, render it.
  if (imageStatus === 'loaded' && gravatarUrl) {
    return (
      <img
        src={gravatarUrl}
        alt={name}
        title={name}
        className={`rounded-full object-cover flex-shrink-0 ${sizeClass} ${className}`}
      />
    );
  }

  // Otherwise, render the fallback with initials.
  const initials = getInitials(name);
  const colorClass = nameToColor(name);

  return (
    <div
      className={`rounded-full flex items-center justify-center font-bold text-white flex-shrink-0 ${sizeClass} ${colorClass} ${className}`}
      title={name}
    >
      {initials}
    </div>
  );
};

export default Avatar;
